import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Flex } from "rebass";
import { FieldProps } from "formik";
import { toastr } from "react-redux-toastr";
import get from "lodash/get";

import { Spinner } from "./Spinner";
import { ParentItem } from "./ParentItem";
import { EntryButton } from "./EntryButton";
import { EntryButtonStyled } from "./EntryButtonStyled";
import { FileList } from "./FileList";

import { Entry, EntryType, ImportType } from "../types";

const nameList = {
  [ImportType.dropbox]: "Dropbox",
  [ImportType.googleDrive]: "Google Drive",
};

enum Status {
  pending = "pending",
  success = "success",
  error = "error",
}

interface IProps extends FieldProps {
  importType: Exclude<ImportType, ImportType.writtenText | ImportType.computerUpload>;
  fetchFileList: (id: string) => Promise<Entry[]>;
}

export const InputFile: React.FC<IProps> = (
  {
    field: { name, value },
    form: { isSubmitting, setFieldValue },
    importType,
    fetchFileList,
  }
) => {
  const [files, setFiles] = useState<Entry[]>([]);
  const [folderStack, setFolderStack] = useState<Entry[]>([]);
  const [status, setStatus] = useState<Status>();

  /* get ID of last folder */
  const activeId = useMemo(() => (
    get(folderStack, [folderStack.length - 1, "id"])
  ), [folderStack]);
  /* get name of last folder */
  const activeName = useMemo(() => (
    get(folderStack, [folderStack.length - 1, "name"], nameList[importType])
  ), [importType, folderStack]);
  /* get parent folder name. Parent is second-to-last in stack. */
  const parentName = useMemo(() => (
    get(folderStack, [folderStack.length - 2, "name"], "Root")
  ), [folderStack]);
  /* add folder to stack */
  const goToFolder = useCallback((folderId: string) => {
    const folder = files.find(file => file.id === folderId);

    if (folder) {
      setFolderStack(prevState => ([...prevState, folder]));
    }
  }, [files]);
  /* handle back to parent folder. remove last entry from stack. */
  const backToParent = useCallback(() => {
    setFolderStack(prevState => prevState.slice(0, -1));
  }, []);
  /* toggle file checked */
  const toggleValue = useCallback((fileId: string) => {
    const fileIdList = value.includes(fileId)
      ? value.filter((v: string) => v !== fileId)
      : [...value, fileId];
    setFieldValue(name, fileIdList);
  }, [name, value, setFieldValue]);
  /*
  * 1. handle click on entry
  * 2. check entry type
  * 3. call goToFolder || toggleValue
  * */
  const onClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault();
    event.stopPropagation();

    const { currentTarget: { value } } = event;
    const [id, mimeType] = value.split(",");

    if (mimeType === EntryType.folder) {
      goToFolder(id);
    } else {
      toggleValue(id);
    }
  }, [toggleValue, goToFolder]);

  /* fetch active folder entries */
  useEffect(() => {
    setStatus(Status.pending);

    fetchFileList(activeId)
      .then((entries) => {
        setFiles((entries));
        setStatus(Status.success);
      })
      .catch(({ message }: Error) => {
        toastr.error(`${importType} GET file list`, message);
        setStatus(Status.error);
      })
  }, [importType, fetchFileList, activeId]);

  return (
    <Flex height="100%" flexDirection="column">
      {activeId && (
        <EntryButtonStyled
          type="button"
          borderRadius="0"
          onClick={backToParent}
        >
          <ParentItem name={parentName} />
        </EntryButtonStyled>
      )}

      <Box flex="1" css="overflow: hidden;">
        {
          status === Status.pending
            ? <Spinner />
            : (
              <FileList
                activeName={activeName}
                isEmpty={!files.length}
              >
                {
                  files.map(({ id, name, type, size, createdTime }) => (
                    <EntryButton
                      key={id}
                      id={id}
                      name={name}
                      type={type}
                      size={size}
                      createdTime={createdTime}
                      checked={value.includes(id)}
                      isDisabled={isSubmitting}
                      onClick={onClick}
                    />
                  ))
                }
              </FileList>
            )
        }
      </Box>
    </Flex>
  );
};
